"use strict";
/**
 * Workflow arrangement — how the steps of a workflow the user arranged by
 * hand take a change without losing that arrangement.
 *
 * A workflow nobody moved a step of is laid out again after every change
 * (`layoutWorkflow`). Once the user moved a step, a change only makes room
 * where it happens: a new step takes its place after the step it follows and
 * the steps behind it move along the flow by the room it needs; a card that
 * grows pushes the steps below it down; a step that goes leaves its gap. The
 * frame of a loop always wraps its body.
 *
 * Pure: boxes in, boxes out, no document.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.stepsFollowing = stepsFollowing;
exports.placeAddedSteps = placeAddedSteps;
exports.growStep = growStep;
exports.arrangeStepGroup = arrangeStepGroup;
exports.fitLoopFrames = fitLoopFrames;
const workflow_canvas_graph_1 = require("./workflow-canvas-graph");
const workflow_layout_1 = require("./workflow-layout");
/** Below this a step counts as beside, not behind. */
const TOLERANCE = 1;
function axesOf(direction) {
    return direction === 'TB'
        ? {
            m: box => box.y,
            c: box => box.x,
            along: box => box.h,
            across: box => box.w,
            place: (box, m, c) => ({ ...box, x: c, y: m }),
        }
        : {
            m: box => box.x,
            c: box => box.y,
            along: box => box.w,
            across: box => box.h,
            place: (box, m, c) => ({ ...box, x: m, y: c }),
        };
}
function union(boxes) {
    if (boxes.length === 0)
        return null;
    const x0 = Math.min(...boxes.map(box => box.x));
    const y0 = Math.min(...boxes.map(box => box.y));
    const x1 = Math.max(...boxes.map(box => box.x + box.w));
    const y1 = Math.max(...boxes.map(box => box.y + box.h));
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
/**
 * The steps that follow `start` along the flow: everything its edges lead to,
 * edges back to a loop aside. A step inside a loop is also followed by what
 * comes after each loop around it.
 */
function stepsFollowing(graph, start) {
    const out = new Map();
    for (const edge of graph.edges) {
        if (edge.kind !== 'flow')
            continue;
        const list = out.get(edge.source) ?? [];
        list.push({ target: edge.target, routeId: edge.routeId });
        out.set(edge.source, list);
    }
    const loopOf = new Map(graph.nodes.map(node => [node.id, node.loopId]));
    const seen = new Set();
    const queue = [];
    const reach = (id) => {
        if (id === start || seen.has(id))
            return;
        seen.add(id);
        queue.push(id);
    };
    for (const edge of out.get(start) ?? [])
        reach(edge.target);
    for (let loop = loopOf.get(start) ?? null; loop !== null; loop = loopOf.get(loop) ?? null) {
        for (const edge of out.get(loop) ?? []) {
            if (edge.routeId !== workflow_canvas_graph_1.LOOP_CONTINUE_ROUTE)
                reach(edge.target);
        }
    }
    while (queue.length > 0) {
        const id = queue.shift();
        for (const edge of out.get(id) ?? [])
            reach(edge.target);
    }
    return seen;
}
/** Moves `ids` that lie at or behind `from` along the flow by `by`. */
function shiftAlong(boxes, ids, from, by, axes) {
    if (by <= 0)
        return;
    for (const id of ids) {
        const box = boxes.get(id);
        if (!box || axes.m(box) < from - TOLERANCE)
            continue;
        boxes.set(id, axes.place(box, axes.m(box) + by, axes.c(box)));
    }
}
/**
 * Places the steps in `added` (their size from `planned`, the layout's boxes)
 * after the step they follow, and moves the steps behind each one along the
 * flow by the room it needs. `boxes` holds the steps that stay, where they lie.
 */
function placeAddedSteps(boxes, graph, added, planned, direction) {
    const axes = axesOf(direction);
    const spacing = (0, workflow_layout_1.workflowLayoutSpacing)(direction);
    const kind = new Map(graph.nodes.map(node => [node.id, node.kind]));
    const flow = graph.edges.filter(edge => edge.kind === 'flow');
    const center = (box) => axes.c(box) + axes.across(box) / 2;
    /** Where a step goes, once a step next to it is placed. */
    const spot = (id) => {
        for (const edge of flow) {
            if (edge.target !== id)
                continue;
            const from = boxes.get(edge.source);
            if (!from)
                continue;
            // Into a loop's body: the first place inside its frame.
            if (kind.get(edge.source) === 'loop' &&
                edge.routeId === workflow_canvas_graph_1.LOOP_CONTINUE_ROUTE) {
                return { m: axes.m(from) + spacing.frameBefore, c: center(from) };
            }
            return {
                m: axes.m(from) + axes.along(from) + spacing.flowGap,
                c: center(from),
            };
        }
        for (const edge of flow) {
            if (edge.source !== id)
                continue;
            const to = boxes.get(edge.target);
            if (to)
                return { m: axes.m(to), c: center(to) };
        }
        return null;
    };
    const pending = added.filter(id => planned.has(id));
    while (pending.length > 0) {
        const ready = pending.findIndex(id => spot(id) !== null);
        const [id] = pending.splice(ready === -1 ? 0 : ready, 1);
        const size = planned.get(id);
        const at = spot(id);
        const all = union([...boxes.values()]);
        // Nothing placed to follow: behind everything, on the line of the rest.
        const m = at?.m ?? (all ? axes.m(all) + axes.along(all) + spacing.flowGap : 0);
        const c = at?.c ?? (all ? center(all) : 0);
        const box = axes.place(size, m, c - axes.across(size) / 2);
        const behind = [...stepsFollowing(graph, id)].filter(other => boxes.has(other) && other !== id);
        const first = Math.min(...behind
            .map(other => axes.m(boxes.get(other)))
            .filter(start => start >= m - TOLERANCE));
        if (Number.isFinite(first)) {
            shiftAlong(boxes, behind, m, m + axes.along(box) + spacing.flowGap - first, axes);
        }
        boxes.set(id, box);
    }
}
/**
 * Sets the height of step `id` to `height`. A card that grows pushes the
 * steps below it down (those whose side it overlaps, and the ones below those)
 * as far as needed to keep the gap of the layout.
 */
function growStep(boxes, id, height, direction) {
    const box = boxes.get(id);
    if (!box)
        return;
    const bottom = box.y + box.h;
    boxes.set(id, { ...box, h: height });
    if (height <= box.h)
        return;
    const spacing = (0, workflow_layout_1.workflowLayoutSpacing)(direction);
    const gap = direction === 'TB' ? spacing.flowGap : spacing.laneGap;
    let x0 = box.x;
    let x1 = box.x + box.w;
    const below = new Set();
    for (let grew = true; grew;) {
        grew = false;
        for (const [other, at] of boxes) {
            if (other === id || below.has(other))
                continue;
            if (at.y < bottom - TOLERANCE)
                continue;
            if (at.x >= x1 || at.x + at.w <= x0)
                continue;
            below.add(other);
            x0 = Math.min(x0, at.x);
            x1 = Math.max(x1, at.x + at.w);
            grew = true;
        }
    }
    const first = Math.min(...[...below].map(other => boxes.get(other).y));
    if (!Number.isFinite(first))
        return;
    const by = box.y + height + gap - first;
    if (by <= 0)
        return;
    for (const other of below) {
        const at = boxes.get(other);
        boxes.set(other, { ...at, y: at.y + by });
    }
}
/**
 * Lays the steps `ids` (and the bodies of loops among them) out as the layout
 * would, keeping the corner where the group starts; the other steps stay.
 */
function arrangeStepGroup(boxes, graph, ids, planned) {
    const group = new Set(ids.filter(id => boxes.has(id)));
    for (let grew = true; grew;) {
        grew = false;
        for (const node of graph.nodes) {
            if (node.loopId && group.has(node.loopId) && !group.has(node.id)) {
                group.add(node.id);
                grew = true;
            }
        }
    }
    const was = union([...group].map(id => boxes.get(id)));
    const plan = union([...group].flatMap(id => {
        const box = planned.get(id);
        return box ? [box] : [];
    }));
    if (!was || !plan)
        return;
    for (const id of group) {
        const box = planned.get(id);
        if (!box)
            continue;
        boxes.set(id, {
            ...box,
            x: box.x - plan.x + was.x,
            y: box.y - plan.y + was.y,
        });
    }
}
/** Every loop's frame wraps its body with the room the layout gives it; an empty loop keeps its box. */
function fitLoopFrames(boxes, graph, direction) {
    const spacing = (0, workflow_layout_1.workflowLayoutSpacing)(direction);
    const parent = new Map(graph.nodes.map(node => [node.id, node.loopId]));
    const depth = (id) => {
        let count = 0;
        for (let at = parent.get(id) ?? null; at; at = parent.get(at) ?? null) {
            count += 1;
        }
        return count;
    };
    const loops = graph.nodes
        .filter(node => node.kind === 'loop')
        .map(node => node.id)
        .sort((a, b) => depth(b) - depth(a));
    for (const loop of loops) {
        const body = union(graph.nodes.flatMap(node => {
            const box = node.loopId === loop ? boxes.get(node.id) : undefined;
            return box ? [box] : [];
        }));
        if (!body)
            continue;
        const [before, after, low, high] = [
            spacing.frameBefore,
            spacing.frameAfter,
            spacing.frameLow,
            spacing.frameHigh,
        ];
        boxes.set(loop, direction === 'TB'
            ? {
                x: body.x - low,
                y: body.y - before,
                w: body.w + low + high,
                h: body.h + before + after,
            }
            : {
                x: body.x - before,
                y: body.y - low,
                w: body.w + before + after,
                h: body.h + low + high,
            });
    }
}
